import UserImage from "@/components/ui/forms/UserImage";

import {useCallback, useEffect, useState} from "react";

import {isEqual} from "@helpers";
import {useReportOptions} from "@hooks/Report";
import {useCountries, useRegionOptions} from "@hooks/WorldCountries";
import photoImage from "@images/cards/photo.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {ChangePassPopup} from "@popups/requestPopups/ChangePassPopup";
import {ReportPopup} from "@popups/requestPopups/ReportPopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {
  clearOptions,
  getCountries,
  getProductLine,
} from "@redux/slices/productSlices/optionsSlice";
import {
  clearEdit,
  getEditUser,
  setError,
  setIsChanged,
  setTarget,
  updateUser,
} from "@redux/slices/userSlices/editSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import styles from "./styles.module.scss";

export const UserEditPage = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const {openPopup} = usePopup();
  const {id} = useParams();
  const {language} = useSelector((state) => state.common.settings);
  const {role} = useSelector((state) => state.common.candidate);
  const {countries: countriesFromApi, productLine} = useSelector(
    (state) => state.product.options,
  );
  const {isChanged, target, initialTarget, status} = useSelector(
    (state) => state.user.edit,
  );
  const countries = useCountries(countriesFromApi);

  const [deletePhoto, setDeletePhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  const {USER_REPORT_OPTIONS} = useReportOptions();

  const selectedCountryName = target?.country;
  const regionOptions = useRegionOptions(selectedCountryName, countriesFromApi);

  const updateUserField = useCallback(
    (fieldName, value) => {
      dispatch((dispatch, getState) => {
        const currentTarget = getState().user.edit.target;

        dispatch(setTarget({...(currentTarget || {}), [fieldName]: value}));
      });
    },
    [dispatch],
  );

  const isFormValid = () => {
    const isAdmin = target.role === "Admin";
    const requiredFields = ["name", "lastname", "email"];
    if (!isAdmin) {
      requiredFields.push("country", "product_line_id");
    }
    return requiredFields.every((field) => target[field] !== "");
  };

  const handleClickImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleClickChangePass = () => {
    openPopup(<ChangePassPopup />);
  };

  const handleDeletePhoto = () => {
    setDeletePhoto(true);
    setPhotoFile(null);
    updateUserField("cover", null);
  };

  const handleClickSave = async () => {
    if (!isFormValid()) {
      openPopup(<FillDataPopup />);
      return;
    }

    const isAdmin = target.role === "Admin";
    const countryObj = countries.find((c) => c.name === target.country);
    const countryId = countryObj ? countryObj.id : "";

    const user = {
      user: {
        name: target.name,
        lastname: target.lastname,
        email: target.email,
        cover: photoFile,
        ...(role === "Admin" &&
          target.password !== "" &&
          target.password_confirmation !== "" && {
            password: target.password,
            password_confirmation: target.password_confirmation,
          }),
        ...(!isAdmin && {
          country_id: countryId,
          region: target.region,
          product_line_id: target.product_line_id,
        }),
        remove_cover: deletePhoto,
      },
    };

    const resultAction = await dispatch(updateUser({id, user}));

    if (updateUser.fulfilled.match(resultAction)) {
      openPopup(<SuccessfulSavePopup title={t("popup.save_data.user")} />);

      URL.revokeObjectURL(photoFile);
      dispatch(setIsChanged(false));
      setPhotoFile(null);
      setDeletePhoto(false);
    } else {
      dispatch(setError("Save error!"));
    }
  };

  useEffect(() => {
    if (target && initialTarget) {
      dispatch(setIsChanged(!isEqual(target, initialTarget)));
    }
  }, [target, initialTarget, dispatch]);

  useEffect(() => {
    dispatch(getEditUser({id}));
    dispatch(getCountries());
    dispatch(getProductLine());

    return () => {
      dispatch(clearOptions());
      dispatch(clearEdit());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (photoFile) {
      dispatch(setIsChanged(true));
      setDeletePhoto(false);
    }

    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
        dispatch(setIsChanged(false));
        setDeletePhoto(false);
      }
    };
  }, [photoFile, target, initialTarget, dispatch]);

  useEffect(() => {
    if (target?.region && regionOptions.length === 0) {
      updateUserField("region", "");
    }
  }, [target?.country, regionOptions.length]);

  return (
    <BorderArticle style={{width: "100%"}}>
      <div className={styles.userWrapper}>
        <div className={styles.userHead}>
          <BackButton />
        </div>

        {status === "loading" ? (
          <Spinner
            width={80}
            height={80}
          />
        ) : (
          <div className={`${styles.user} ${styles.userBig}`}>
            <div className={styles.userInfo}>
              <EditTitle
                label={"user-name"}
                value={target?.name}
                onChange={(e) => updateUserField("name", e.target.value)}
                placeholder={t("input.user_name_placeholder")}
                handleClickSave={handleClickSave}
                disabledButton={!isChanged}
              />

              <UserImage
                onClickDelete={handleDeletePhoto}
                photoFile={photoFile || target?.metadata}
                editUser={target}
                photoImage={photoImage}
                onClick={handleClickImage}
              />
            </div>

            <div className={styles.content}>
              <ul>
                <EditField
                  label={`${t("input.last_name")}*`}
                  value={target?.lastname}
                  onChange={(e) => updateUserField("lastname", e.target.value)}
                />

                <EditField
                  label={`${t("input.email")}*`}
                  value={target?.email}
                  onChange={(e) => updateUserField("email", e.target.value)}
                />

                {role === "Admin" ? (
                  <>
                    <EditField
                      label={`${t("input.password")}*`}
                      placeholder={"*********"}
                      value={target?.password}
                      onChange={(e) =>
                        updateUserField("password", e.target.value)
                      }
                    />

                    <EditField
                      label={`${t("input.confirm_password")}*`}
                      placeholder={"*********"}
                      value={target?.password_confirmation}
                      onChange={(e) =>
                        updateUserField("password_confirmation", e.target.value)
                      }
                    />
                  </>
                ) : (
                  <ViewField
                    label={`${t("input.password")}*`}
                    customContent={
                      <div className={styles.userField}>
                        <RootDesc>
                          <span>
                            **********
                            <i className={styles.userEditPassword}>
                              <ControlButton
                                color={"var(--blue)"}
                                txt={"Edit"}
                                onClick={handleClickChangePass}
                              />
                            </i>
                          </span>
                        </RootDesc>
                      </div>
                    }
                  />
                )}

                <ViewField
                  label={`${t("input.role")}*`}
                  value={target?.role}
                />

                {target?.role !== "Admin" && (
                  <>
                    {productLine && productLine.length > 0 && (
                      <EditField
                        label={`${t("input.product_line")}*`}
                        width={280}
                        height={150}
                        searchPlaceholder={t("input.product_line_search")}
                        activeOption={target?.product_line}
                        options={productLine}
                        onSelect={(selectedOption) =>
                          updateUserField("product_line_id", selectedOption?.id)
                        }
                        type={"select"}
                      />
                    )}

                    <EditField
                      label={`${t("input.country")}*`}
                      width={280}
                      height={150}
                      searchPlaceholder={t("input.country_search")}
                      activeOption={target?.country}
                      options={countries}
                      onSelect={(selectedOption) => {
                        updateUserField("country", selectedOption?.name);
                        updateUserField("region", "");
                      }}
                      emptyResult={
                        <span
                          onClick={() =>
                            openPopup(
                              <ReportPopup options={USER_REPORT_OPTIONS} />,
                            )
                          }
                          dangerouslySetInnerHTML={{
                            __html: t("input.countries_regions_empty"),
                          }}
                        />
                      }
                      type={"select"}
                    />

                    {language === "ru" && selectedCountryName && (
                      <EditField
                        label={t("input.region")}
                        width={280}
                        height={150}
                        searchPlaceholder={t("input.region_search")}
                        activeOption={target?.region}
                        options={
                          Array.isArray(regionOptions) ? regionOptions : []
                        }
                        onSelect={(selectedOption) =>
                          updateUserField("region", selectedOption?.name)
                        }
                        emptyResult={
                          <span
                            onClick={() =>
                              openPopup(
                                <ReportPopup options={USER_REPORT_OPTIONS} />,
                              )
                            }
                            dangerouslySetInnerHTML={{
                              __html: t("input.countries_regions_empty"),
                            }}
                          />
                        }
                        type={"select"}
                        selectKey={`region-${selectedCountryName || "none"}`}
                        resetKey={`${selectedCountryName || "none"}-${regionOptions?.length || 0}`}
                      />
                    )}
                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </BorderArticle>
  );
};
